import { TFunction } from 'i18next';
import {
  EuqFeedbackRecord,
  FeedbackRecordStatus,
  FeedbackMitigationStatus,
} from '../../type';
import { FeedbackCategoryStrMap } from '../../constants';
import { getUtcTime } from '@Utils/time';
import { SERVICE_NAME_ID_MAP } from '@Utils/service';
import { ServiceNameEnum } from '@Types/index';

const SERVICE_TYPE_OUTLOOK = [
  SERVICE_NAME_ID_MAP[ServiceNameEnum.Exchange],
  SERVICE_NAME_ID_MAP[ServiceNameEnum.ExchangeInline],
] as const;

const SERVICE_TYPE_GMAIL = [
  SERVICE_NAME_ID_MAP[ServiceNameEnum.Gmail],
  SERVICE_NAME_ID_MAP[ServiceNameEnum.GmailInline],
] as const;

export interface FormattedCecpData {
  id: string;
  created_at: string;
  message_arrive_time: string;
  message_subject: string;
  message_sender: string;
  message_recipients: string;
  requested_by: string;
  category: string;
  approval_status: string;
  gtoc_result_category: string;
  mitigation_status: string;
  service_type: string;
  request_source: string;
  company_guid: string;
  status: FeedbackRecordStatus;
  // Keep original data for table rendering logic
  originalData: EuqFeedbackRecord;
}

export const formatCecpRecord = (
  record: EuqFeedbackRecord,
  offset: string,
  timeFormat: string,
  t: TFunction
): FormattedCecpData => {
  const FeedbackMitigationStrMap = {
    [FeedbackMitigationStatus.DONE]: t(
      'operations.euq.action.mitigationCompleted'
    ),
    [FeedbackMitigationStatus.IN_PROGRESS]: t(
      'operations.euq.action.autoMitigationInProgress'
    ),
    [FeedbackMitigationStatus.PARTIAL_INPROGRESS]: t(
      'operations.euq.action.autoMitigationInProgress'
    ),
    [FeedbackMitigationStatus.INITIALIZED]: t(
      'operations.euq.action.mitigationRequired'
    ),
    [FeedbackMitigationStatus.INITIALIZING]: '-',
  };

  // Format analysis result category
  let analysisResult = '-';
  if (
    [
      FeedbackRecordStatus.APPROVED,
      FeedbackRecordStatus.APPROVED_DONE,
    ].includes(record.status)
  ) {
    const resultCategory = record.gtoc_result_category;
    if (resultCategory) {
      const completedLabels: Record<string, string> = {
        spam: t('operations.euq.analysis.completedSpam'),
        threat: t('operations.euq.analysis.completedPhishing'),
        flagged: t('operations.euq.analysis.completedFlagged'),
        normal: t('operations.euq.analysis.completedNotARisk'),
      };
      analysisResult = completedLabels[resultCategory] || '-';
    }
  } else if (record.status === FeedbackRecordStatus.APPROVED_IN_PROGRESS) {
    analysisResult = t('operations.euq.analysis.inProgress');
  } else if (record.status === FeedbackRecordStatus.APPROVED_FAILED) {
    analysisResult = t('operations.euq.analysis.failed');
  }

  // Format mitigation status
  const mitigationStatus =
    FeedbackMitigationStrMap[record.mitigation_status] || '-';

  // Format approval status
  let approvalStatus = '-';
  if (
    [
      FeedbackRecordStatus.APPROVED,
      FeedbackRecordStatus.APPROVED_DONE,
      FeedbackRecordStatus.APPROVED_IN_PROGRESS,
      FeedbackRecordStatus.APPROVED_FAILED,
    ].includes(record.status)
  ) {
    approvalStatus = t('operations.euq.statusTab.approved', '-');
  } else {
    approvalStatus = t(`operations.euq.statusTab.${record.status}`, '-');
  }

  let serviceTypeLabel = '-';
  if (SERVICE_TYPE_OUTLOOK.includes(record.service_type)) {
    serviceTypeLabel = t('common:common.service.exchange');
  } else if (SERVICE_TYPE_GMAIL.includes(record.service_type)) {
    serviceTypeLabel = t('common:common.service.gmail');
  }

  // Format Collect source
  const requestSourceLabelKey =
    record.request_source === 'AutoCollect'
      ? 'administration.addIn.autoCollect.collectSource.autoCollect'
      : record.request_source === 'Banner'
        ? 'administration.addIn.autoCollect.collectSource.banner'
        : record.request_source === 'CentralOperation'
          ? 'administration.addIn.autoCollect.collectSource.adminReport'
          : 'administration.addIn.autoCollect.collectSource.plugin';
  const requestSource = t(requestSourceLabelKey);

  return {
    id: record.id.toString(),
    created_at: getUtcTime(record.created_at, offset, timeFormat),
    message_arrive_time: getUtcTime(
      record.message_arrive_time,
      offset,
      timeFormat
    ),
    message_subject: record.message_subject || '',
    message_sender: record.message_sender || '',
    message_recipients: record.message_recipients || '',
    requested_by: record.requested_by || '',
    category: t(
      FeedbackCategoryStrMap[
        record.category as keyof typeof FeedbackCategoryStrMap
      ],
      record.category
    ),
    approval_status: approvalStatus,
    gtoc_result_category: analysisResult,
    mitigation_status: mitigationStatus,
    service_type: serviceTypeLabel,
    request_source: requestSource,
    company_guid: record.company_guid,
    status: record.status,
    originalData: record,
  };
};

export const formatCecpRecords = (
  records: EuqFeedbackRecord[],
  offset: string,
  timeFormat: string,
  t: TFunction
): FormattedCecpData[] => {
  return records.map((record) =>
    formatCecpRecord(record, offset, timeFormat, t)
  );
};

// Helper function to determine if a record should link to details
export const shouldLinkToDetails = (record: EuqFeedbackRecord): boolean => {
  return (
    record.status === FeedbackRecordStatus.APPROVED_DONE &&
    !!record.gtoc_result_category
  );
};

// Helper function to determine if mitigation action should link to details
export const shouldMitigationLinkToDetails = (
  record: EuqFeedbackRecord
): boolean => {
  return record.mitigation_status === FeedbackMitigationStatus.INITIALIZED;
};
